import { memo, useState, Suspense, lazy } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import styled from '@mui/material/styles/styled';
import FormHelperText from '@mui/material/FormHelperText';

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import IconButton from '@mui/material/IconButton';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import MenuItem from '@mui/material/MenuItem';

import { ErrorMessage } from '@hookform/error-message';
import { useWatch } from 'react-hook-form';

import DebouncedTextField from '../debounced-text-field';
import SupportingQuestionInputs from '../supporting-question-inputs';
import Select from '../../../../../components/inputs/select';
import ConditionalQuestionLogic from '../conditional-question-logic';
import Colors from '../../../../../styles/colors';
import IconList from '../../../../../wiring/icon-map';
import { calculateQuestionStats } from '../../../../../utils/question-statistics';

const StyledPaper = styled(Paper)({
    borderRadius: 10,
    border: `1px solid ${Colors.lavenderGray}`,
    padding: 25,
    width: '100%',
});

const StyledHR = styled('hr')({
    border: 0,
    borderTop: `1px solid ${Colors.lavenderGray}`,
    marginLeft: -25,
    marginRight: -25,
    marginTop: 25,
    marginBottom: 25,
});

const SimpleButton = styled('button')(({ theme }) => ({
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.primary.main,
}));

const LeftSideBorderElement = styled('div')({
    backgroundColor: Colors.lavenderGray,
    width: 1,
    height: '100%',
    marginRight: 20,
    marginLeft: 5,
});

const questionTypes = [
    { key: 'shortText', label: 'Short Answer', helperText: 'Max 200 characters' },
    { key: 'longText', label: 'Long Answer', helperText: 'Max 500 characters' },
    { key: 'date', label: 'Date Entry (MM/DD/YYYY)' },
    { key: 'time', label: 'Time Entry' },
    { key: 'radioButton', label: 'Radio Button' },
    { key: 'checkBoxes', label: 'Checkboxes' },
    { key: 'repeatingGroup', label: 'Repeating Group', helperText: 'All children will be a collection of questions' },
    { key: 'file', label: 'File Upload', helperText: 'File types supported: .pdf, .doc, .xls' },
    {
        key: 'instructions',
        label: 'Instructions',
        helperText: 'This will not be a question, but will be displayed as a header',
    },
];

const questionTypesWithoutRepeatingGroup = questionTypes.filter(({ key }) => key !== 'repeatingGroup');

// Lazy load the heavy accordion content to improve performance
const AccordionContent = lazy(() =>
    Promise.resolve({
        default: ({
            isExpanded,
            questionIndex,
            watchedSection,
            sectionIndex,
            control,
            errors,
            setValue,
            register,
            question,
            questionTypes: questionTypesFromProps,
            questionTypesWithoutRepeatingGroup: questionTypesWithoutRepeatingGroupFromProps,
            handleAddQuestion,
            handleDeleteQuestionModal,
            handleChildQuestionOrder,
            handleGrandchildQuestionOrder,
            getValues,
        }) => (
            <AccordionDetails sx={{ p: 3, position: 'relative' }}>
                <Grid container spacing={3}>
                    <Grid item xs={5}>
                        <DebouncedTextField
                            errors={errors}
                            name={`sections[${sectionIndex}].questions[${questionIndex}].text`}
                            label="Question Text"
                            required
                            control={control}
                            inputProps={{
                                maxLength: 500,
                            }}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <Select
                            errors={errors}
                            name={`sections[${sectionIndex}].questions[${questionIndex}].type`}
                            label="Question Type"
                            control={control}
                            placeholder="Select"
                            displayEmpty
                            renderValue={(value) =>
                                value !== '' &&
                                questionTypesFromProps.find(({ key }) => key.toString() === value.toString())?.label
                            }
                        >
                            {questionTypesFromProps.map((option) => (
                                <MenuItem value={option.key} key={option.label}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Grid>
                    <Grid item xs={12}>
                        <SupportingQuestionInputs
                            question={watchedSection?.questions?.[questionIndex]}
                            fieldName={`sections[${sectionIndex}].questions[${questionIndex}]`}
                            control={control}
                            errors={errors}
                            setValue={setValue}
                            register={register}
                        />
                    </Grid>

                    {/* CHILD QUESTION LOOP - Only render when accordion is expanded */}
                    {isExpanded &&
                        watchedSection?.questions?.[questionIndex]?.questions?.map(
                            (childQuestion, childQuestionIndex) => (
                                <Grid
                                    item
                                    container
                                    key={`${question.id} - ${childQuestion.id}`}
                                    style={{ marginLeft: 45 }}
                                >
                                    <Grid
                                        item
                                        xs="auto"
                                        style={{ display: 'flex', flexDirection: 'column' }}
                                        sx={{ mt: childQuestionIndex > 0 ? 3 : 1 }}
                                    >
                                        <IconButton
                                            onClick={(evt) => {
                                                evt.stopPropagation();
                                                handleChildQuestionOrder(
                                                    sectionIndex,
                                                    questionIndex,
                                                    childQuestionIndex,
                                                    childQuestionIndex - 1,
                                                );
                                            }}
                                            disabled={childQuestionIndex === 0}
                                        >
                                            <ArrowUpwardIcon style={{ color: Colors.darkGrey2 }} />
                                        </IconButton>
                                        <IconButton
                                            onClick={(evt) => {
                                                evt.stopPropagation();
                                                handleChildQuestionOrder(
                                                    sectionIndex,
                                                    questionIndex,
                                                    childQuestionIndex,
                                                    childQuestionIndex + 1,
                                                );
                                            }}
                                            disabled={childQuestionIndex === question.questions.length - 1}
                                        >
                                            <ArrowDownwardIcon style={{ color: Colors.darkGrey2 }} />
                                        </IconButton>
                                    </Grid>
                                    <Grid item xs="auto">
                                        <LeftSideBorderElement />
                                    </Grid>
                                    <Grid item xs container sx={{ mt: childQuestionIndex > 0 && 2 }}>
                                        <Grid item container xs={11} spacing={3}>
                                            <Grid item container xs={12}>
                                                <ConditionalQuestionLogic
                                                    fieldName={`sections[${sectionIndex}].questions[${questionIndex}].questions[${childQuestionIndex}].renderLogic`}
                                                    selectedCondition={
                                                        watchedSection?.questions?.[questionIndex]?.questions?.[
                                                            childQuestionIndex
                                                        ]?.renderLogic?.condition
                                                    }
                                                    parentQuestion={watchedSection?.questions?.[questionIndex]}
                                                    control={control}
                                                    errors={errors}
                                                    register={register}
                                                    getValues={getValues}
                                                    setValue={setValue}
                                                />
                                            </Grid>
                                            <Grid item xs={5}>
                                                <DebouncedTextField
                                                    errors={errors}
                                                    name={`sections[${sectionIndex}].questions[${questionIndex}].questions[${childQuestionIndex}].text`}
                                                    label="Question Text"
                                                    required
                                                    control={control}
                                                    inputProps={{
                                                        maxLength: 500,
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Select
                                                    errors={errors}
                                                    name={`sections[${sectionIndex}].questions[${questionIndex}].questions[${childQuestionIndex}].type`}
                                                    label="Question Type"
                                                    control={control}
                                                    placeholder="Select"
                                                    displayEmpty
                                                    renderValue={(value) =>
                                                        questionTypesFromProps.find(
                                                            ({ key }) => key.toString() === value.toString(),
                                                        )?.label
                                                    }
                                                >
                                                    {questionTypesFromProps.map((option) => (
                                                        <MenuItem value={option.key} key={option.label}>
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <SupportingQuestionInputs
                                                    question={
                                                        watchedSection?.questions?.[questionIndex]?.questions?.[
                                                            childQuestionIndex
                                                        ]
                                                    }
                                                    fieldName={`sections[${sectionIndex}].questions[${questionIndex}].questions[${childQuestionIndex}]`}
                                                    control={control}
                                                    errors={errors}
                                                    setValue={setValue}
                                                    register={register}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid item xs style={{ display: 'flex', alignItems: 'start' }}>
                                            <IconButton
                                                onClick={() =>
                                                    handleDeleteQuestionModal({
                                                        sectionIndex,
                                                        questionIndex,
                                                        childQuestionIndex,
                                                    })
                                                }
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Grid>
                                        {/* GRANDCHILD QUESTION LOOP */}
                                        {watchedSection?.questions?.[questionIndex]?.questions?.[
                                            childQuestionIndex
                                        ]?.questions?.map((grandchildQuestion, grandchildQuestionIndex) => (
                                            <Grid
                                                item
                                                container
                                                key={`${question.id} - ${childQuestion.id}-${grandchildQuestion.id}`}
                                            >
                                                <Grid
                                                    item
                                                    xs="auto"
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                    }}
                                                    sx={{
                                                        mt: childQuestionIndex > 0 ? 3 : 1,
                                                    }}
                                                >
                                                    <IconButton
                                                        onClick={(evt) => {
                                                            evt.stopPropagation();
                                                            handleGrandchildQuestionOrder(
                                                                sectionIndex,
                                                                questionIndex,
                                                                childQuestionIndex,
                                                                grandchildQuestionIndex,
                                                                grandchildQuestionIndex - 1,
                                                            );
                                                        }}
                                                        disabled={grandchildQuestionIndex === 0}
                                                    >
                                                        <KeyboardArrowUpIcon
                                                            style={{
                                                                color: Colors.darkGrey2,
                                                            }}
                                                        />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={(evt) => {
                                                            evt.stopPropagation();
                                                            handleGrandchildQuestionOrder(
                                                                sectionIndex,
                                                                questionIndex,
                                                                childQuestionIndex,
                                                                grandchildQuestionIndex,
                                                                grandchildQuestionIndex + 1,
                                                            );
                                                        }}
                                                        disabled={
                                                            grandchildQuestionIndex ===
                                                            (watchedSection?.questions?.[questionIndex]?.questions?.[
                                                                childQuestionIndex
                                                            ]?.questions?.length ?? 0) -
                                                                1
                                                        }
                                                    >
                                                        <KeyboardArrowDownIcon
                                                            style={{
                                                                color: Colors.darkGrey2,
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Grid>
                                                <Grid item xs="auto" style={{ marginLeft: -15 }}>
                                                    <LeftSideBorderElement style={{ marginLeft: 15 }} />
                                                </Grid>
                                                <Grid item xs container>
                                                    <Grid item container xs={11} spacing={3}>
                                                        <Grid item container xs={12}>
                                                            <ConditionalQuestionLogic
                                                                fieldName={`sections[${sectionIndex}].questions[${questionIndex}].questions[${childQuestionIndex}].questions[${grandchildQuestionIndex}].renderLogic`}
                                                                selectedCondition={
                                                                    watchedSection?.questions?.[questionIndex]
                                                                        ?.questions?.[childQuestionIndex]?.questions?.[
                                                                        grandchildQuestionIndex
                                                                    ]?.renderLogic?.condition
                                                                }
                                                                parentQuestion={
                                                                    watchedSection?.questions?.[questionIndex]
                                                                        ?.questions?.[childQuestionIndex]
                                                                }
                                                                control={control}
                                                                errors={errors}
                                                                register={register}
                                                                getValues={getValues}
                                                                setValue={setValue}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={5}>
                                                            <DebouncedTextField
                                                                errors={errors}
                                                                name={`sections[${sectionIndex}].questions[${questionIndex}].questions[${childQuestionIndex}].questions[${grandchildQuestionIndex}].text`}
                                                                label="Question Text"
                                                                required
                                                                control={control}
                                                                inputProps={{
                                                                    maxLength: 500,
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={3}>
                                                            <Select
                                                                errors={errors}
                                                                name={`sections[${sectionIndex}].questions[${questionIndex}].questions[${childQuestionIndex}].questions[${grandchildQuestionIndex}].type`}
                                                                label="Question Type"
                                                                control={control}
                                                                placeholder="Select"
                                                                displayEmpty
                                                                renderValue={(value) =>
                                                                    questionTypesFromProps.find(
                                                                        ({ key }) =>
                                                                            key.toString() === value.toString(),
                                                                    )?.label
                                                                }
                                                            >
                                                                {questionTypesWithoutRepeatingGroupFromProps.map(
                                                                    (option) => (
                                                                        <MenuItem value={option.key} key={option.label}>
                                                                            {option.label}
                                                                        </MenuItem>
                                                                    ),
                                                                )}
                                                            </Select>
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <SupportingQuestionInputs
                                                                question={
                                                                    watchedSection?.questions?.[questionIndex]
                                                                        ?.questions?.[childQuestionIndex]?.questions?.[
                                                                        grandchildQuestionIndex
                                                                    ]
                                                                }
                                                                fieldName={`sections[${sectionIndex}].questions[${questionIndex}].questions[${childQuestionIndex}].questions[${grandchildQuestionIndex}]`}
                                                                control={control}
                                                                errors={errors}
                                                                setValue={setValue}
                                                                register={register}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                    <Grid
                                                        item
                                                        xs
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'start',
                                                        }}
                                                    >
                                                        <IconButton
                                                            onClick={() =>
                                                                handleDeleteQuestionModal({
                                                                    sectionIndex,
                                                                    questionIndex,
                                                                    childQuestionIndex,
                                                                    grandchildQuestionIndex,
                                                                })
                                                            }
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        ))}
                                        {watchedSection?.questions?.[questionIndex]?.questions?.[childQuestionIndex]
                                            ?.type !== 'instructions' && (
                                            <Grid item xs={12}>
                                                <SimpleButton
                                                    type="button"
                                                    onClick={() =>
                                                        handleAddQuestion(
                                                            sectionIndex,
                                                            questionIndex,
                                                            childQuestionIndex,
                                                        )
                                                    }
                                                >
                                                    <AddCircleOutlineIcon
                                                        style={{
                                                            marginBottom: 5,
                                                            marginRight: 5,
                                                            fontSize: 17,
                                                        }}
                                                    />
                                                    {watchedSection?.questions?.[questionIndex]?.questions?.[
                                                        childQuestionIndex
                                                    ]?.type === 'repeatingGroup'
                                                        ? 'Add Question to Group'
                                                        : 'Add Conditional Question'}
                                                </SimpleButton>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Grid>
                            ),
                        )}
                    <Grid item xs={12} mt={3}>
                        {watchedSection?.questions?.[questionIndex]?.type !== 'instructions' && (
                            <SimpleButton type="button" onClick={() => handleAddQuestion(sectionIndex, questionIndex)}>
                                <AddCircleOutlineIcon
                                    style={{
                                        marginBottom: 5,
                                        marginRight: 5,
                                        fontSize: 17,
                                    }}
                                />
                                {watchedSection?.questions?.[questionIndex]?.type === 'repeatingGroup'
                                    ? 'Add Question to Group'
                                    : 'Add Conditional Question'}
                            </SimpleButton>
                        )}
                    </Grid>
                </Grid>
            </AccordionDetails>
        ),
    }),
);

const Section = memo(
    ({
        item,
        sectionIndex,
        totalSections,
        control,
        errors,
        register,
        setValue,
        getValues,
        handleAddQuestion,
        handleDeleteQuestionModal,
        handleDeleteSectionModal,
        handleEditSectionIcon,
        handleQuestionOrder,
        handleChildQuestionOrder,
        handleGrandchildQuestionOrder,
        move,
    }) => {
        // Watch only this section's data
        const watchedSection = useWatch({ control, name: `sections[${sectionIndex}]` });

        // Accordion state - only allow one question group to be expanded at a time
        // Track by question ID instead of index to maintain focus during reordering
        const [expandedQuestionId, setExpandedQuestionId] = useState(null);

        // Handle accordion expansion - only one can be open at a time
        const handleQuestionAccordionChange = (questionId) => (event, isExpanded) => {
            if (isExpanded) {
                setExpandedQuestionId(questionId);
            } else {
                setExpandedQuestionId(null);
            }
        };

        // Get icon for display
        let selectedIcon = '';
        if (watchedSection?.icon) {
            const iconObject = IconList.find(({ key }) => key === watchedSection.icon);
            if (iconObject) {
                selectedIcon = iconObject.value;
            }
        }

        return (
            <Grid item container xs={12} pt={5}>
                <StyledPaper>
                    <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<KeyboardArrowDownIcon />}>
                            <Grid item container xs={12}>
                                <Grid item xs="auto" style={{ display: 'flex', flexDirection: 'column' }}>
                                    <IconButton
                                        onClick={(evt) => {
                                            evt.stopPropagation();
                                            move(sectionIndex, sectionIndex - 1);
                                        }}
                                        disabled={sectionIndex === 0}
                                    >
                                        <ArrowUpwardIcon style={{ color: Colors.darkGrey2 }} />
                                    </IconButton>
                                    <IconButton
                                        onClick={(evt) => {
                                            evt.stopPropagation();
                                            move(sectionIndex, sectionIndex + 1);
                                        }}
                                        disabled={sectionIndex === totalSections - 1}
                                    >
                                        <ArrowDownwardIcon style={{ color: Colors.darkGrey2 }} />
                                    </IconButton>
                                </Grid>
                                <Grid item xs={6} pr={3}>
                                    <DebouncedTextField
                                        errors={errors}
                                        name={`sections[${sectionIndex}].name`}
                                        label="Section Name"
                                        required
                                        control={control}
                                        onClick={(evt) => evt.stopPropagation()}
                                    />
                                </Grid>
                                <Grid
                                    item
                                    xs="auto"
                                    style={{
                                        display: 'flex',
                                        flexDirection: watchedSection?.icon ? 'row' : 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginTop: 15,
                                    }}
                                >
                                    <input name={`sections[${sectionIndex}].icon`} {...register} type="hidden" />
                                    {watchedSection?.icon && (
                                        <>
                                            <div
                                                style={{
                                                    backgroundColor: Colors.greyLightest,
                                                    borderRadius: 3,
                                                    border: `1px solid ${Colors.greyLight}`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: 40,
                                                    height: 40,
                                                    marginRight: 15,
                                                }}
                                            >
                                                <img src={selectedIcon} alt="icon" style={{ height: 30 }} />
                                            </div>
                                            <SimpleButton
                                                type="button"
                                                onClick={(evt) => {
                                                    evt.stopPropagation();
                                                    handleEditSectionIcon(
                                                        `sections[${sectionIndex}].icon`,
                                                        sectionIndex,
                                                    );
                                                }}
                                            >
                                                Edit Section Icon
                                            </SimpleButton>
                                        </>
                                    )}
                                    {!watchedSection?.icon && (
                                        <>
                                            <SimpleButton
                                                type="button"
                                                onClick={(evt) => {
                                                    evt.stopPropagation();
                                                    handleEditSectionIcon(
                                                        `sections[${sectionIndex}].icon`,
                                                        sectionIndex,
                                                    );
                                                }}
                                            >
                                                Select Section Icon
                                            </SimpleButton>
                                            <FormHelperText
                                                id={`sections[${sectionIndex}].icon-error`}
                                                role="alert"
                                                aria-live="polite"
                                                error
                                            >
                                                <ErrorMessage name={`sections[${sectionIndex}].icon`} errors={errors} />
                                            </FormHelperText>
                                        </>
                                    )}
                                </Grid>
                                <Grid
                                    item
                                    xs
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'end',
                                        marginRight: 30,
                                        marginTop: 15,
                                    }}
                                >
                                    <IconButton
                                        onClick={(evt) => {
                                            evt.stopPropagation();
                                            handleDeleteSectionModal(sectionIndex);
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </AccordionSummary>
                        <Grid item xs={12}>
                            <StyledHR />
                        </Grid>
                        {watchedSection?.questions?.map((question, questionIndex) => {
                            const isExpanded = expandedQuestionId === question.id;
                            // Calculate question statistics without useMemo since this is inside a map
                            const questionStats = calculateQuestionStats(question);

                            return (
                                <Grid item xs={12} key={`section-${item.id}-${question.id}`} mt={2}>
                                    <Accordion
                                        expanded={isExpanded}
                                        onChange={handleQuestionAccordionChange(question.id)}
                                        sx={{
                                            border: `1px solid ${Colors.lavenderGray}`,
                                            borderRadius: '8px !important',
                                            '&:before': { display: 'none' },
                                            boxShadow: 'none',
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<KeyboardArrowDownIcon />}
                                            sx={{
                                                backgroundColor: Colors.antiflashWhite,
                                                borderRadius: '8px',
                                                '&.Mui-expanded': {
                                                    borderBottomLeftRadius: 0,
                                                    borderBottomRightRadius: 0,
                                                },
                                            }}
                                        >
                                            <Grid container alignItems="center" spacing={2}>
                                                <Grid item xs="auto">
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleQuestionOrder(
                                                                sectionIndex,
                                                                questionIndex,
                                                                questionIndex - 1,
                                                            );
                                                        }}
                                                        disabled={questionIndex === 0}
                                                        size="small"
                                                    >
                                                        <ArrowUpwardIcon style={{ color: Colors.darkGrey2 }} />
                                                    </IconButton>
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleQuestionOrder(
                                                                sectionIndex,
                                                                questionIndex,
                                                                questionIndex + 1,
                                                            );
                                                        }}
                                                        disabled={questionIndex === watchedSection.questions.length - 1}
                                                        size="small"
                                                    >
                                                        <ArrowDownwardIcon style={{ color: Colors.darkGrey2 }} />
                                                    </IconButton>
                                                </Grid>
                                                <Grid item xs>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                        Question {questionIndex + 1}:{' '}
                                                        {question.text || 'Untitled Question'}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {questionStats} total questions • Type:{' '}
                                                        {questionTypes.find((t) => t.key === question.type)?.label ||
                                                            question.type}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs="auto">
                                                    <IconButton
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteQuestionModal({ sectionIndex, questionIndex });
                                                        }}
                                                        size="small"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        </AccordionSummary>

                                        {isExpanded && (
                                            <Suspense fallback={<div />}>
                                                <AccordionContent
                                                    isExpanded={isExpanded}
                                                    questionIndex={questionIndex}
                                                    watchedSection={watchedSection}
                                                    sectionIndex={sectionIndex}
                                                    control={control}
                                                    errors={errors}
                                                    setValue={setValue}
                                                    register={register}
                                                    question={question}
                                                    questionTypes={questionTypes}
                                                    questionTypesWithoutRepeatingGroup={
                                                        questionTypesWithoutRepeatingGroup
                                                    }
                                                    handleAddQuestion={handleAddQuestion}
                                                    handleDeleteQuestionModal={handleDeleteQuestionModal}
                                                    handleChildQuestionOrder={handleChildQuestionOrder}
                                                    handleGrandchildQuestionOrder={handleGrandchildQuestionOrder}
                                                    getValues={getValues}
                                                />
                                            </Suspense>
                                        )}
                                    </Accordion>
                                </Grid>
                            );
                        })}
                        <Grid item xs={12}>
                            <hr />
                        </Grid>
                        <Grid item xs={12} pt={5}>
                            <SimpleButton type="button" onClick={() => handleAddQuestion(sectionIndex)}>
                                <AddCircleOutlineIcon style={{ marginBottom: 5, marginRight: 5, fontSize: 17 }} />
                                Add Question
                            </SimpleButton>
                        </Grid>
                    </Accordion>
                </StyledPaper>
            </Grid>
        );
    },
    // Custom comparison function to prevent unnecessary re-renders
    (prevProps, nextProps) =>
        // Only re-render if essential props have changed
        prevProps.sectionIndex === nextProps.sectionIndex &&
        prevProps.totalSections === nextProps.totalSections &&
        prevProps.item === nextProps.item && // Reference equality should be sufficient
        prevProps.errors === nextProps.errors &&
        // Don't compare functions as they may be recreated but are functionally the same
        prevProps.control === nextProps.control,
);

Section.displayName = 'Section';

Section.propTypes = {
    item: PropTypes.object.isRequired,
    sectionIndex: PropTypes.number.isRequired,
    totalSections: PropTypes.number.isRequired,
    control: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    register: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired,
    getValues: PropTypes.func.isRequired,
    handleAddQuestion: PropTypes.func.isRequired,
    handleDeleteQuestionModal: PropTypes.func.isRequired,
    handleDeleteSectionModal: PropTypes.func.isRequired,
    handleEditSectionIcon: PropTypes.func.isRequired,
    handleQuestionOrder: PropTypes.func.isRequired,
    handleChildQuestionOrder: PropTypes.func.isRequired,
    handleGrandchildQuestionOrder: PropTypes.func.isRequired,
    move: PropTypes.func.isRequired,
};

export default Section;
